/**
 * CrmStreamLoadModal — "Stream to DB" (issue #735): lets a single CRM/SaaS
 * object be loaded straight into a target DB table batch-by-batch, without
 * first extracting a full CSV via Parse & Continue. Requires a Target
 * Platform connection to already be established (this modal never manages
 * its own target DB connection — always reuses the shared one).
 */

import { AlertTriangle, ArrowRight, Loader2, X } from "lucide-react";
import type { CrmToDbLoadStatusResponse, SalesforceObjectMeta } from "../apiv2";

// Mirrors the backend's ZTD_CRM_TO_DB_MAX_BATCH_SIZE default (schemas.py) --
// client-side clamp only, the server enforces this regardless.
const MAX_BATCH_SIZE = 20000;

export interface CrmStreamLoadConfig {
  targetSchema: string;
  targetTable: string;
  batchSize: number;
  truncate: boolean;
  fieldMappings: {
    source_field: string;
    target_column: string;
    value_map: string;
  }[];
}

function ModalInputField({
  label,
  value,
  onChange,
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-white/50 mb-1.5">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 bg-surface-400/50 border border-white/10 rounded-lg text-sm text-white placeholder-white/20 focus:outline-none focus:border-brand-500/50 transition"
      />
    </div>
  );
}

export function CrmStreamLoadModal({
  object,
  config,
  onConfigChange,
  hasTargetConnection,
  starting,
  jobId,
  status,
  error,
  onErrorDismiss,
  onStart,
  onCancel,
  onClose,
}: {
  object: SalesforceObjectMeta;
  config: CrmStreamLoadConfig;
  onConfigChange: React.Dispatch<React.SetStateAction<CrmStreamLoadConfig>>;
  hasTargetConnection: boolean;
  starting: boolean;
  jobId: string | null;
  status: CrmToDbLoadStatusResponse | null;
  error: string | null;
  onErrorDismiss: () => void;
  onStart: () => void;
  onCancel: () => void;
  onClose: () => void;
}) {
  const running = !!jobId && (!status || status.status === "running");
  const updateMapping = (
    index: number,
    patch: Partial<{ target_column: string; value_map: string }>,
  ) => {
    onConfigChange((prev) => ({
      ...prev,
      fieldMappings: prev.fieldMappings.map((m, i) =>
        i === index ? { ...m, ...patch } : m,
      ),
    }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[85vh] bg-surface-500 border border-white/10 rounded-xl shadow-2xl p-5 space-y-4 overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            <ArrowRight size={16} className="text-brand-400" />
            Stream "{object.label}" to Target DB
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-white/5 text-white/50 hover:text-white transition"
          >
            <X size={16} />
          </button>
        </div>
        <p className="text-xs text-white/40">
          Streams this object's records directly into a target database table,
          batch-by-batch — never materializes the full object in memory or as an
          intermediate CSV, regardless of size.
        </p>
        {!hasTargetConnection && (
          <p className="text-xs text-amber-400/80">
            Connect to a target database in the Target Platform step below, then
            reopen this dialog.
          </p>
        )}
        {error && (
          <div className="flex items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
            <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
            <span className="max-h-40 overflow-y-auto whitespace-pre-wrap break-words">
              {error}
            </span>
            <button
              onClick={onErrorDismiss}
              aria-label="Dismiss error"
              className="ml-auto flex-shrink-0 text-red-300/70 hover:text-red-200 transition"
            >
              <X size={14} />
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <ModalInputField
            label="Target Schema"
            value={config.targetSchema}
            onChange={(v) =>
              onConfigChange((prev) => ({ ...prev, targetSchema: v }))
            }
            required
          />
          <ModalInputField
            label="Target Table"
            value={config.targetTable}
            onChange={(v) =>
              onConfigChange((prev) => ({ ...prev, targetTable: v }))
            }
            required
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
          <ModalInputField
            label="Batch Size"
            value={String(config.batchSize)}
            onChange={(v) =>
              onConfigChange((prev) => ({
                ...prev,
                batchSize: Math.min(
                  MAX_BATCH_SIZE,
                  Math.max(1, Number(v) || 2000),
                ),
              }))
            }
          />
          <label className="flex items-center gap-2 text-xs text-white/60 pb-1.5">
            <input
              type="checkbox"
              className="accent-brand-500"
              checked={config.truncate}
              onChange={(e) =>
                onConfigChange((prev) => ({
                  ...prev,
                  truncate: e.target.checked,
                }))
              }
            />
            Truncate target table before loading
          </label>
        </div>

        <div className="rounded-lg border border-white/10">
          <div className="border-b border-white/10 px-3 py-2 text-xs text-white/60">
            Field Mappings ({config.fieldMappings.length})
          </div>
          <div className="max-h-56 overflow-y-auto divide-y divide-white/5">
            {config.fieldMappings.map((m, i) => (
              <div
                key={m.source_field}
                className="grid grid-cols-[1fr_1fr_1fr] gap-2 px-3 py-1.5 items-center"
              >
                <span
                  className="text-[11px] text-white/50 truncate"
                  title={m.source_field}
                >
                  {m.source_field}
                </span>
                <input
                  value={m.target_column}
                  onChange={(e) =>
                    updateMapping(i, { target_column: e.target.value })
                  }
                  placeholder="target column"
                  className="ztd-input text-xs"
                />
                <input
                  value={m.value_map}
                  onChange={(e) =>
                    updateMapping(i, { value_map: e.target.value })
                  }
                  placeholder="value map (optional)"
                  className="ztd-input text-xs"
                  title="Optional Transform-engine expression or plain-English instruction, e.g. 'UPPER(FirstName)' or 'M as Male, F as Female'"
                />
              </div>
            ))}
          </div>
        </div>

        {status && (
          <div className="rounded-lg border border-white/10 px-3 py-2 text-xs text-white/70 space-y-1">
            <div className="flex items-center justify-between">
              <span>Status</span>
              <span
                className={
                  status.status === "failed"
                    ? "text-red-400"
                    : status.status === "completed"
                      ? "text-green-400"
                      : "text-white/60"
                }
              >
                {status.status}
              </span>
            </div>
            <div>Batches done: {status.batches_done}</div>
            <div>
              Rows read / loaded / failed: {status.rows_read} /{" "}
              {status.rows_loaded} / {status.rows_failed}
            </div>
            {status.errors.length > 0 && (
              <div className="text-red-400/80">{status.errors[0]}</div>
            )}
          </div>
        )}

        <div className="flex items-center gap-3">
          {running ? (
            <button onClick={onCancel} className="ztd-btn ztd-btn-secondary">
              Cancel Load
            </button>
          ) : (
            <button
              onClick={onStart}
              disabled={starting || !hasTargetConnection}
              className="ztd-btn ztd-btn-primary"
            >
              {starting ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <ArrowRight size={14} />
              )}
              Start Streaming Load
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
